import { useEffect } from "react";
import { cn } from "@/lib/utils";
import { PermissionsEnum } from "../../../../helpers/constants";
import DatastoreCard from "./DatastoreCard";
import DbtCloudCard from "./DbtCloudCard";
import DbtCoreCard from "./DbtCoreCard";
import { DatastoreResponse, DbtCloudConnectionResponse } from "../../types";
import { Integration } from "@/types/integration";
import useAppContext, { useAppState } from "@/modules/app/useAppContext";
import useAppActions from "@/modules/app/useAppActions";
import { useSearchParams } from "react-router-dom";
import { toast } from "@altimateai/lego";

interface DatastoreListProps {
  datastores: DatastoreResponse;
  dbtCloudConnections?: DbtCloudConnectionResponse[];
  dbtCoreIntegrations?: Integration[];
}

const DatastoreList = ({
  datastores,
  dbtCloudConnections = [],
  dbtCoreIntegrations = [],
}: DatastoreListProps): JSX.Element => {
  const totalItems =
    datastores.data_stores.length + dbtCloudConnections.length + dbtCoreIntegrations.length;
  const useCompactGrid = totalItems <= 2;
  const { loggedinUser: user } = useAppState();
  const { dispatch } = useAppContext();
  const { fetchDatastores } = useAppActions(dispatch);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const refreshParam = searchParams.get("refresh");
    if (refreshParam === "true") {
      fetchDatastores();
      // Remove refresh param from URL
      searchParams.delete("refresh");
      setSearchParams(searchParams);
      toast({
        title: "Connection added successfully",
        description:
          "Data Ingestion will start and it will take a few hours to complete. Please reach out to Altimate team for updates.",
        duration: 30000,
      });
    }
  }, [searchParams, fetchDatastores, setSearchParams]);

  const syncOpAllowed = Boolean(user?.permission?.includes(PermissionsEnum.DATASTORE_EDIT));

  return (
    <div
      className={cn(
        "al-grid al-gap-8",
        useCompactGrid
          ? "al-grid-cols-[repeat(auto-fit,minmax(600px,650px))]"
          : "al-grid-cols-[repeat(auto-fit,minmax(600px,1fr))]"
      )}
    >
      {datastores.data_stores.map(datastore => (
        <DatastoreCard key={datastore.id} datastore={datastore} syncOpAllowed={syncOpAllowed} />
      ))}
      {dbtCloudConnections.map(connection => (
        <DbtCloudCard
          key={`dbt-cloud-${connection.id}`}
          connection={connection}
          syncOpAllowed={syncOpAllowed}
        />
      ))}
      {dbtCoreIntegrations.map(integration => (
        <DbtCoreCard key={`dbt-core-${integration.id}`} integration={integration} />
      ))}
    </div>
  );
};

export default DatastoreList;
